import { useEffect, useRef } from 'react';
import { RouteInfo } from '../../types/fare';
import { decodePolyline } from '../../utils/polylineUtils';

interface RawPolylinesProps {
  map: google.maps.Map | null;
  routeInfo: RouteInfo;
  selectedRouteIndex: number;
  onRouteSelect: (index: number) => void;
  mapId?: string;
}

export default function RawPolylines({
  map,
  routeInfo,
  selectedRouteIndex,
  onRouteSelect,
  mapId = 'unknown',
}: RawPolylinesProps) {
  const polylinesRef = useRef<google.maps.Polyline[]>([]);
  const { recommended, alternatives } = routeInfo;
  const allRoutes = [recommended, ...alternatives];
  const routeColors = ['#10b981', '#3b82f6', '#ef4444', '#eab308']; // Green, Blue, Red, Yellow

  useEffect(() => {
    if (!map) {
      return;
    }

    // Create polylines directly using Google Maps API
    const newPolylines = allRoutes.map((route, index) => {
      if (!route.polyline) return null;

      const path = decodePolyline(route.polyline);
      const isSelected = selectedRouteIndex === index;
      const isRecommended = index === 0;

      const polyline = new google.maps.Polyline({
        path,
        strokeColor: isRecommended ? '#10b981' : routeColors[index % routeColors.length],
        strokeOpacity: isSelected ? 1 : 0.5,
        strokeWeight: isSelected ? 6 : 4,
        clickable: true,
        zIndex: isSelected ? 100 : 50 - index,
        map, // Attach to map immediately
      });

      // Add click listener
      polyline.addListener('click', () => {
        onRouteSelect(index);
      });

      return polyline;
    }).filter((p): p is google.maps.Polyline => p !== null);

    polylinesRef.current = newPolylines;

    // Cleanup function
    return () => {
      polylinesRef.current.forEach((polyline, idx) => {
        if (polyline) {
          // Remove listeners
          google.maps.event.clearInstanceListeners(polyline);
          // Clear path
          polyline.setPath([]);
          // Hide
          polyline.setVisible(false);
          // Remove from map
          polyline.setMap(null);
        }
      });
      
      polylinesRef.current = [];
    };
  }, [map, routeInfo, selectedRouteIndex, allRoutes.length, mapId, onRouteSelect]);

  return null; // This component doesn't render anything in React
}

